import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  Inject,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ButtonComponent } from '../../molecules/button/button.component';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { IconComponent } from '../../atoms/icon/icon.component';
import { ModalFunctionComponent } from '../../molecules/modal-function/modal-function.component';
import { StaticGraphicComponent } from '../../molecules/static-graphic/static-graphic.component';
import { MultimediaListComponent } from '../multimedia-list/multimedia-list.component';
import { UtilitiesCardsComponent } from '../../molecules/utilities-cards/utilities-cards.component';
import { ModalFunctionType } from '../../../shared/enums/modal-function-type.enums';
import { TextBlockComponent } from '../../atoms/text-block/text-block.component';
import { AccordionItem } from '../../../shared/interfaces/accordion-item.interface';
import { MultimediaItem } from '../../../shared/interfaces/multimedia-item.interface';
import { ModalFunctionItem } from '../../../shared/interfaces/modal-function-item.interface';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MultimediaList } from '../../../shared/interfaces/multimedia-list.interface';
import { UtilitiesCards } from '../../../shared/interfaces/utilities-cards.interface';
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeService } from '../../../services/fontawesome.service';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { TextBlock } from '../../../shared/interfaces/text-block.interface';
import { ImagesService } from '../../../services/images.service';
import { Router } from '@angular/router';
import { CapitalizePipe } from '../../capitalize.pipe';

@Component({
  selector: 'hdi-accordion',
  standalone: true,
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    ModalFunctionComponent,
    MultimediaListComponent,
    UtilitiesCardsComponent,
    TextBlockComponent,
    StaticGraphicComponent,
    CapitalizePipe,
  ],
  animations: [
    trigger('rotateAnimation', [
      state('default', style({ transform: 'rotate(0deg)' })),
      state('open', style({ transform: 'rotate(90deg)' })),
      state('clicked', style({ transform: 'rotate(90deg)' })),
      transition('default <=> open', animate('250ms ease-in-out')),
      transition('default <=> clicked', animate('250ms ease-out')),
    ]),
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class AccordionComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() headerLabel: string = 'Accordion';
  @Input() accordionItems!: AccordionItem[];
  @Input() MultimediaItems!: MultimediaItem[];
  @Input() multimediaLists!: MultimediaList[];
  @Input() utilitiesCardsList!: UtilitiesCards[];
  @Input() textBlocks?: TextBlock[];
  @Input() graphs?: CaasComponent[];
  @Input() modalFunctionItems!: ModalFunctionItem[];
  @Input() title: string | undefined = '';
  @Input() storybook: boolean = false;
  @Input() caasData!: CaasComponent;
  @Input() expandState?: string;
  @Input() isSectionBg?: boolean;
  @Input() hasIcon: boolean = false;
  @ViewChild('accordion_content', { static: true })
  isOpen: boolean[] = [];
  accordion: ButtonType = ButtonType.OPEN_CLOSE_SECONDARY;
  AccordionToggle!: ElementRef;
  ModalFunctionType = ModalFunctionType;
  animationStates: string[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private fontAwesomeService: FontAwesomeService,
    private caasRoutesService: CaasRoutesService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private imagesService: ImagesService
  ) {}

  ngOnInit() {
    this.isOpen = this.accordionItems.map(() => false);
  }

  ngAfterViewInit() {
    this.animationStates = this.isOpen.map((isOpen) =>
      isOpen ? 'clicked' : 'default'
    );
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent();
      this.setCaasOpenState();
    }
  }

  toggleAnimation(index: number) {
    this.animationStates = this.animationStates.map((state, i) =>
      i === index ? (state === 'default' ? 'clicked' : 'default') : 'default'
    );
  }

  toggleOpenClose(index: number) {
    if (this.isOpen[index]) {
      this.isOpen[index] = false;
      this.updateAnimationStates(index);
      this.cdr.detectChanges();
    } else {
      this.isOpen.fill(false);
      this.isOpen[index] = true;
      this.updateAnimationStates(index);
      this.cdr.detectChanges();
    }
  }

  getIcon(name: string): string {
    const isIconAvailable = this.fontAwesomeService.checkIconAvailable(
      'fal' as IconPrefix,
      name as IconName
    );
    if (isIconAvailable !== null) {
      return `${name}`;
    } else {
      return `user`;
    }
  }

  getRightIcon(rightIcon: string) {
    if (rightIcon.length) {
      switch (rightIcon) {
        case 'tooltip':
          return 'info-circle';
        case 'modal':
          return 'list';
        default:
          return rightIcon;
      }
    } else {
      return '';
    }
  }

  setAccordionItemsAndGraphs() {
    if (this.caasData?.formData?.st_items?.value?.length) {
      this.accordionItems = [];

      this.caasData.formData.st_items.value.forEach((item) => {
        const key = item.identifier;
        const content: CaasComponent[] = [];

        // Extract nested items in order
        if (item.formData?.st_items?.value?.length) {
          item.formData.st_items.value.forEach((childItem) => {
            content.push({
              ...childItem,
              identifier: key,
            });
          });
        }

        // Push accordion item with its content
        this.accordionItems.push({
          key,
          label: item.formData?.st_header?.value || '',
          name: item.formData?.st_header?.value || '',
          icon: item.formData?.st_icon?.value?.toString() || 'user',
          Headline: item.formData?.st_header?.value || '',
          content, // Include ordered content
        });
      });
    }
  }

  // getMultimediaRightIcons(link: CaasMultimediaListLink) {
  //   if (link?.formData?.lt_link_type?.value?.identifier) {
  //     switch (link.formData.lt_link_type.value.identifier) {
  //       case 'external':
  //         return [
  //           {
  //             type: 'arrow-up-right-from-square',
  //             href: link?.formData?.lt_link_external?.value ?? '',
  //             target: '_blank',
  //             tooltip: {
  //               id: crypto.randomUUID().toString(),
  //               description: '<span>Öffnen</span>',
  //             },
  //             title: 'Öffnen',
  //           },
  //           {
  //             type: 'link',
  //             href: link?.formData?.lt_link_external?.value ?? '',
  //             tooltip: {
  //               id: crypto.randomUUID().toString(),
  //               description: '<span>Link kopieren</span>',
  //             },
  //             title: 'Link kopieren',
  //           },
  //         ];
  //       case 'internal':
  //         return [
  //           {
  //             type: 'right',
  //             href:
  //               this.caasRoutesService.getSeoRouteByIdentifier(
  //                 link?.formData?.lt_link_internal?.value?.identifier ?? '',
  //                 this.router.config
  //               )?.path ?? '404',
  //             target: '_self',
  //             tooltip: {
  //               id: crypto.randomUUID().toString(),
  //               description: '<span>Öffnen</span>',
  //             },
  //             title: 'Öffnen',
  //           },
  //           {
  //             type: 'link',
  //             href:
  //               this.getInternalHref(
  //                 link?.formData?.lt_link_internal?.value?.identifier
  //               ) ?? '404',
  //             target: '_self',
  //             tooltip: {
  //               id: crypto.randomUUID().toString(),
  //               description: '<span>Link kopieren</span>',
  //             },
  //             title: 'Link kopieren',
  //           },
  //         ];
  //       case 'contact': {
  //         const rightIcons: {
  //           type: string;
  //           href: string;
  //           tooltip?: Tooltip;
  //           title?: string;
  //         }[] = [];
  //         if (link?.formData?.lt_email_address?.value) {
  //           rightIcons.push({
  //             type: 'envelope',
  //             href: 'mailto:' + link?.formData?.lt_email_address?.value,
  //             tooltip: {
  //               id: crypto.randomUUID().toString(),
  //               description: '<span>E-mail senden</span>',
  //             },
  //             title: 'E-mail senden',
  //           });
  //         }
  //         if (link?.formData?.lt_telephone?.value) {
  //           rightIcons.push({
  //             type: 'phone',
  //             href: 'tel:' + link?.formData?.lt_telephone?.value,
  //             tooltip: {
  //               id: crypto.randomUUID().toString(),
  //               description: '<span>Anrufen</span>',
  //             },
  //             title: 'Anrufen',
  //           });
  //         }
  //         return rightIcons;
  //       }
  //       case 'download':
  //         if (link?.formData?.lt_link_external?.value) {
  //           if (
  //             link?.formData?.lt_link_external?.value.includes(
  //               'https://www.hdi-brandportal.com'
  //             )
  //           ) {
  //             return [
  //               {
  //                 type: 'eye',
  //                 href: link?.formData?.lt_link_external?.value ?? '',
  //                 tooltip: {
  //                   id: crypto.randomUUID().toString(),
  //                   description: '<span>Ansicht</span>',
  //                 },
  //                 title: 'Ansicht',
  //               },
  //               {
  //                 type: 'link',
  //                 href: link?.formData?.lt_link_external?.value ?? '',
  //                 tooltip: {
  //                   id: crypto.randomUUID().toString(),
  //                   description: '<span>Link kopieren</span>',
  //                 },
  //                 title: 'Link kopieren',
  //               },
  //             ];
  //           } else {
  //             return [
  //               {
  //                 type: 'eye',
  //                 href: link?.formData?.lt_link_external?.value ?? '',
  //                 tooltip: {
  //                   id: crypto.randomUUID().toString(),
  //                   description: '<span>Ansicht</span>',
  //                 },
  //                 title: 'Ansicht',
  //               },
  //               {
  //                 type: 'down-to-line',
  //                 href: link?.formData?.lt_link_external?.value ?? '',
  //                 tooltip: {
  //                   id: crypto.randomUUID().toString(),
  //                   description: '<span>Download</span>',
  //                 },
  //                 title: 'Download',
  //               },
  //               {
  //                 type: 'link',
  //                 href: link?.formData?.lt_link_external?.value ?? '',
  //                 tooltip: {
  //                   id: crypto.randomUUID().toString(),
  //                   description: '<span>Link kopieren</span>',
  //                 },
  //                 title: 'Link kopieren',
  //               },
  //             ];
  //           }
  //         }
  //     }
  //   }
  //   return [];
  // }

  // getMultimediaLeftIcon(leftIcon: string) {
  //   if (leftIcon.length) {
  //     switch (leftIcon) {
  //       case 'file-pdf':
  //         return 'file-pdf';
  //       default:
  //         return leftIcon;
  //     }
  //   } else {
  //     return '';
  //   }
  // }

  // setTextBlocks() {
  //   if (this.caasData?.formData?.st_items?.value?.length) {
  //     this.textBlocks = this.caasData.formData.st_items.value
  //       .map((textSection) => ({
  //         header: textSection.formData?.st_header?.value ?? '',
  //         key: textSection.identifier,
  //         items: textSection.formData?.st_items?.value,
  //       }))
  //       .filter((textBlock) => {
  //         return textBlock.items?.some(
  //           (item) => item.template.uid === 'st_text'
  //         );
  //       })
  //       .flatMap((textBlock) => ({
  //         header: textBlock.header,
  //         key: textBlock.key,
  //         items: textBlock.items?.filter(
  //           (item) => item.template.uid === 'st_text'
  //         ),
  //       }));
  //   }
  // }

  setCaasOpenState() {
    this.isOpen = new Array(this.accordionItems?.length ?? 0).fill(false);
    this.expandState = this.caasData?.formData?.st_expand?.value?.identifier;
    switch (this.expandState) {
      case 'firstOpen':
        this.isOpen[0] = true;
        this.animationStates = new Array(this.accordionItems?.length ?? 0).fill(
          'default'
        );
        this.animationStates[0] = 'open';
        break;
      case 'allOpen':
        this.isOpen.fill(true);
        this.animationStates = new Array(this.accordionItems?.length ?? 0).fill(
          'open'
        );
        break;
      case 'allClosed':
        this.isOpen.fill(false);
        this.animationStates = new Array(this.accordionItems?.length ?? 0).fill(
          'default'
        );
        break;
      default:
        this.isOpen.fill(false);
        this.animationStates = new Array(this.accordionItems?.length ?? 0).fill(
          'default'
        );
        break;
    }
  }
  filterModalFunctionItems(key: string) {
    if (key) {
      return this.modalFunctionItems.filter((item) => item.key === key);
    } else {
      return this.modalFunctionItems;
    }
  }

  // // First filter for accordion section and second for multimedia section
  // filterMultimediaItems(key: string) {
  //   let filteredItems = this.MultimediaItems;

  //   if (key) {
  //     filteredItems = filteredItems.filter((item) => item.key === key);
  //   }

  //   const groupedBySectionId = filteredItems.reduce(
  //     (acc, item) => {
  //       const sectionId = item.sectionId ?? '';

  //       if (!acc[sectionId]) {
  //         acc[sectionId] = [];
  //       }
  //       acc[sectionId].push(item);
  //       return acc;
  //     },
  //     {} as { [sectionId: string]: MultimediaItem[] }
  //   );
  //   return Object.values(groupedBySectionId);
  // }
  //   setMultimediaLists() {
  //         if (this.caasData?.formData?.st_items?.value?.length) {
  //       this.multimediaLists = this.caasData.formData.st_items.value
  //       .flatMap(
  //         (card) =>
  //         card?.formData?.st_items?.value?.map((item) => ({
  //             title: item?.formData?.st_header?.value ?? '',
  //             key: card.identifier,
  //             items:
  //               item?.formData?.st_links?.value?.map((link) => ({
  //             label: link?.formData?.lt_link_label?.value ?? '',
  //             link: '',
  //             title: link?.formData?.lt_link_title?.value ?? '',
  //             leftIcon: this.getMultimediaLeftIcon(
  //               link?.formData?.lt_icon?.value?.identifier ?? ''
  //             ),
  //             rightIcons: this.getMultimediaRightIcons(link),
  //           })) ?? [],
  //           })) ?? []
  //       )
  //       .filter((item) => item.items.length);
  //   }
  // }

  setutilitiesCardsList() {
    if (this.caasData?.formData?.st_items?.value?.length) {
      this.utilitiesCardsList = this.caasData.formData.st_items.value
        .flatMap(
          (card) =>
            card?.formData?.st_items?.value?.map((item) => {
              return {
                title: item?.formData?.st_header?.value ?? undefined,
                key: card.identifier,
                items:
                  item?.formData?.st_items?.value?.map((link) => {
                    return {
                      label: link?.formData?.st_header?.value ?? '',
                      rightIcon: this.getRightIcon(
                        link?.formData?.st_infotype?.value?.identifier ?? ''
                      ),
                      badgeLabel: link?.formData?.st_badge?.value ?? undefined,
                      tooltip: link.formData?.st_tooltip?.value?.length
                        ? {
                            id: link.identifier,
                            description: `<p>${link.formData?.st_tooltip?.value}</p>`,
                          }
                        : undefined,
                      target:
                        link?.formData?.st_link?.value?.formData?.lt_link_type
                          ?.value?.identifier === 'external'
                          ? '_blank'
                          : '_self',
                      url:
                        link?.formData?.st_link?.value?.formData?.lt_link_type
                          ?.value?.identifier === 'external'
                          ? link?.formData?.st_link?.value?.formData
                              ?.lt_link_external?.value
                          : (this.caasRoutesService.getSeoRouteByIdentifier(
                              link?.formData?.st_link?.value?.formData
                                ?.lt_link_internal?.value?.identifier ?? '',
                              this.router.config
                            )?.path ?? '404'),
                      title:
                        link?.formData?.st_link?.value?.formData?.lt_link_title
                          ?.value ?? '',
                      modal:
                        link?.formData?.st_modal_headline?.value ||
                        link?.formData?.st_modal_text?.value?.length
                          ? {
                              title:
                                link?.formData?.st_modal_headline?.value ?? '',
                              description: `<p>${link?.formData?.st_modal_text?.value}</p>`,
                            }
                          : undefined,
                    };
                  }) ?? [],
              };
            }) ?? []
        )
        .filter((item) => item.items.length);
    }
  }
  // Update animation states based on open states
  private updateAnimationStates(index: number) {
    if (this.animationStates[index] === 'default') {
      this.animationStates.map((state, i) => {
        if (state === 'open') {
          this.animationStates[i] = 'default';
        }
      });
    }
    this.animationStates[index] = this.isOpen[index] ? 'open' : 'default';
  }

  setCaasContent() {
    if (this.caasData?.formData?.st_header) {
      this.title = this.caasData.formData.st_header?.value;
    }
    if (this.caasData?.formData?.st_show_icons?.value) {
      this.hasIcon = this.caasData.formData.st_show_icons.value;
    }
    this.setutilitiesCardsList();
    // this.setMultimediaLists();
    this.setAccordionItemsAndGraphs();
  }
  getInternalHref(pageIdentifier?: string) {
    if (pageIdentifier) {
      const baseElement = this.document.querySelector('base');
      return (
        baseElement?.href +
        '' +
        this.caasRoutesService.getSeoRouteByIdentifier(
          pageIdentifier ?? '',
          this.router.config
        )?.path
      );
    }
    return '404';
  }
}
